// Builds the content for the custom page appended to the bottom of a deal's
// document. The output is a plain, serializable data structure (no React, no
// styling) so the SAME function can run on the backend to generate the actual
// PDF and on the frontend to render the live preview.
//
// Keep this module dependency-free and framework-agnostic.

const hasValue = v => v !== "" && v !== null && v !== undefined;

export const formatCurrency = value =>
  !hasValue(value)
    ? "N/A"
    : `$${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

export const formatPercent = value => (!hasValue(value) ? "N/A" : `${value}%`);

/**
 * @typedef {Object} TableColumn
 * @property {string} key
 * @property {string} label
 * @property {"left"|"right"} [align]
 *
 * @typedef {Object} TableSection
 * @property {string} id
 * @property {"table"} type
 * @property {string} title
 * @property {TableColumn[]} columns
 * @property {Object<string,string>[]} rows
 *
 * @typedef {Object} AppendedPageContent
 * @property {TableSection[]} sections
 */

// Build a scale's appended-page sections: the template's document text (when
// present) shown above the scale table (revenue -> percentage). Returns [] when
// the scale has no filled-in lines. The first section carries the heading.
// Shared by every scale type on the appended page.
function buildScaleSections({
  id,
  title,
  rateLabel,
  lines = [],
  documentText,
}) {
  const filled = lines.filter(
    l => hasValue(l.revenue_amount) || hasValue(l.percentage)
  );
  if (filled.length === 0) return [];

  const sections = [];

  if (hasValue(documentText)) {
    sections.push({ id: `${id}-document`, type: "text", body: documentText });
  }

  sections.push({
    id,
    type: "table",
    columns: [
      { key: "revenue", label: "Revenue", align: "left" },
      { key: "rate", label: rateLabel, align: "right" },
    ],
    rows: filled.map(line => ({
      revenue: hasValue(line.revenue_amount)
        ? formatCurrency(line.revenue_amount)
        : "—",
      rate: hasValue(line.percentage) ? formatPercent(line.percentage) : "—",
    })),
  });

  sections[0] = { ...sections[0], title };
  return sections;
}

// Upfront pay types that are driven by scale lines (account number -> amount or
// percentage of ACV).
const UPFRONT_SCALE_TYPES = [
  "SCALE_AMOUNT_PER_ACCOUNT",
  "SCALE_AMOUNT_TOTAL",
  "SCALE_PERCENT_ACV",
];

// Scale table for an upfront pay option (account number -> amount/percentage),
// or null when no lines are filled in.
function buildUpfrontScaleSection({ type, lines = [] }) {
  const isPercent = type === "SCALE_PERCENT_ACV";
  const filled = lines.filter(
    l =>
      hasValue(l.accounts_amount) ||
      hasValue(l.amount) ||
      hasValue(l.percentage)
  );
  if (filled.length === 0) return null;

  return {
    id: "upfront-scale",
    type: "table",
    columns: [
      { key: "accounts", label: "Account Number", align: "left" },
      {
        key: "value",
        label: isPercent ? "Percentage of ACV" : "Amount",
        align: "right",
      },
    ],
    rows: filled.map(line => ({
      accounts: hasValue(line.accounts_amount)
        ? String(line.accounts_amount)
        : "—",
      value: isPercent
        ? hasValue(line.percentage)
          ? formatPercent(line.percentage)
          : "—"
        : hasValue(line.amount)
          ? formatCurrency(line.amount)
          : "—",
    })),
  };
}

// Upfront pay section(s): the option's document text first, then the type's
// detail (fixed amount, fixed percentage, or scale lines). Returns [] when
// there's nothing to show. The whole group gets one "Upfront Pay" heading.
function buildUpfrontSections({
  documentText,
  type,
  fixedAmount,
  fixedPercentage,
  scaleLines = [],
} = {}) {
  const sections = [];

  if (hasValue(documentText)) {
    sections.push({ id: "upfront-document", type: "text", body: documentText });
  }

  if (type === "FIXED_AMOUNT_PER_ACCOUNT") {
    if (hasValue(fixedAmount)) {
      sections.push({
        id: "upfront-fixed-amount",
        type: "text",
        body: `Fixed Amount Per Account: ${formatCurrency(fixedAmount)}`,
      });
    }
  } else if (type === "FIXED_PERCENT_ACV") {
    if (hasValue(fixedPercentage)) {
      sections.push({
        id: "upfront-fixed-percentage",
        type: "text",
        body: `Fixed Percentage of ACV: ${formatPercent(fixedPercentage)}`,
      });
    }
  } else if (UPFRONT_SCALE_TYPES.includes(type)) {
    const scaleSection = buildUpfrontScaleSection({ type, lines: scaleLines });
    if (scaleSection) sections.push(scaleSection);
  }

  if (sections.length === 0) return [];
  sections[0] = { ...sections[0], title: "Upfront Pay" };
  return sections;
}

/**
 * Build the appended-page content from deal form values.
 *
 * @param {Object} input
 * @param {string} input.dealType - e.g. "MD", "REP", "OVERRIDE"
 * @param {Array<{revenue_amount: string|number, percentage: string|number}>} [input.mdLines]
 * @param {Array<{revenue_amount: string|number, percentage: string|number}>} [input.commissionLines]
 * @param {Array<{revenue_amount: string|number, percentage: string|number}>} [input.overrideLines]
 * @param {string} [input.mdDocumentText] - document text of the selected MD scale
 * @param {string} [input.commissionDocumentText] - document text of the selected commission scale
 * @param {string} [input.overrideDocumentText] - document text of the selected override scale
 * @param {Object} [input.upfront] - selected upfront pay option + edited values
 * @param {string} [input.upfront.documentText]
 * @param {string} [input.upfront.type] - the upfront_pay_type
 * @param {string|number} [input.upfront.fixedAmount]
 * @param {string|number} [input.upfront.fixedPercentage]
 * @param {Array<{accounts_amount: string|number, amount: string|number, percentage: string|number}>} [input.upfront.scaleLines]
 * @returns {AppendedPageContent}
 */
export function buildAppendedPageContent({
  dealType,
  mdLines = [],
  commissionLines = [],
  overrideLines = [],
  mdDocumentText,
  commissionDocumentText,
  overrideDocumentText,
  upfront = null,
} = {}) {
  const sections = [
    ...(dealType === "MD"
      ? buildScaleSections({
          id: "md-scale",
          title: "Marketing Deal Scale",
          rateLabel: "Marketing Deal Rate",
          lines: mdLines,
          documentText: mdDocumentText,
        })
      : []),
    ...buildScaleSections({
      id: "commission-scale",
      title: "Commission Scale",
      rateLabel: "Commission Rate",
      lines: commissionLines,
      documentText: commissionDocumentText,
    }),
    ...buildScaleSections({
      id: "override-scale",
      title: "Override Scale",
      rateLabel: "Override Rate",
      lines: overrideLines,
      documentText: overrideDocumentText,
    }),
    ...(upfront ? buildUpfrontSections(upfront) : []),
  ].filter(Boolean);

  return { sections };
}
